import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type { RenderOptions, ReportWindow, SessionPatch } from './api';
import { useTryOnDeps } from './context';
import type { Conversion, NailArtDesign, Render, Shade, TryOnSession } from './schemas';
import { isSettled } from './schemas';

/**
 * The TanStack Query layer.
 *
 * **Nothing here invalidates the shade list.** A sitting records what the
 * client tried; it changes no stock and withdraws no SKU, so refetching the
 * list after every tap would be a request for data nobody has changed — and
 * on a phone in the middle of a render loop, the one network call that
 * would cost a frame.
 *
 * **Opening a session is a mutation, not a query.** It writes a row, it must
 * not be retried by a cache warm-up, and the screen needs its id exactly
 * once.
 */
export const tryOnKeys = {
  all: ['tryon'] as const,
  shades: () => [...tryOnKeys.all, 'shades'] as const,
  conversion: (window?: ReportWindow) =>
    [...tryOnKeys.all, 'conversion', window?.start ?? '', window?.end ?? ''] as const,
  designs: () => [...tryOnKeys.all, 'designs'] as const,
  render: (renderId: string) => [...tryOnKeys.all, 'render', renderId] as const,
};

/** How often the screen asks whether the picture is ready, in milliseconds. */
export const RENDER_POLL_MS = 1500;

export function useShades(): UseQueryResult<Shade[]> {
  const { api } = useTryOnDeps();
  return useQuery({ queryKey: tryOnKeys.shades(), queryFn: () => api.listShades() });
}

export function useOpenSession(): UseMutationResult<TryOnSession, Error, void> {
  const { api, mode } = useTryOnDeps();
  return useMutation({ mutationFn: () => api.openSession({ mode }) });
}

export function usePatchSession(): UseMutationResult<
  TryOnSession,
  Error,
  { sessionId: string; patch: SessionPatch }
> {
  const { api } = useTryOnDeps();
  return useMutation({
    mutationFn: ({ sessionId, patch }) => api.patchSession(sessionId, patch),
  });
}

export function useConversion(window?: ReportWindow): UseQueryResult<Conversion> {
  const { api } = useTryOnDeps();
  return useQuery({
    queryKey: tryOnKeys.conversion(window),
    queryFn: () => api.conversion(window),
  });
}

export function useDesigns(): UseQueryResult<NailArtDesign[]> {
  const { api } = useTryOnDeps();
  return useQuery({ queryKey: tryOnKeys.designs(), queryFn: () => api.listDesigns() });
}

export function useCreateRender(): UseMutationResult<Render, Error, RenderRequest> {
  const { api } = useTryOnDeps();
  return useMutation({
    mutationFn: ({ photo, options }) => api.createRender(photo, options),
  });
}

export interface RenderRequest {
  photo: Blob;
  options?: RenderOptions;
}

/**
 * Poll one render until it stops moving.
 *
 * `refetchInterval` returns `false` once the render is settled, which is
 * what stops the asking: a fixed interval would keep a phone awake all
 * afternoon on a picture that was finished in four seconds, and a client
 * who leaves the tab open is the normal case.
 *
 * `renderId` of `null` disables the query outright rather than fetching a
 * route with "null" in it — the screen has no render until she uploads one.
 */
export function useRender(renderId: string | null): UseQueryResult<Render> {
  const { api } = useTryOnDeps();
  return useQuery({
    queryKey: tryOnKeys.render(renderId ?? ''),
    queryFn: () => api.readRender(renderId as string),
    enabled: renderId !== null,
    refetchInterval: (query) => {
      const render = query.state.data;
      return render !== undefined && isSettled(render) ? false : RENDER_POLL_MS;
    },
  });
}
